import { useCallback, useEffect, useState } from "react";
import { SettingsSection } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { ChevronRight } from "lucide-react";
import { Button, Checkbox, Field, Input, Select, Textarea, ConfirmButton } from "../ui";
import { useSession } from "./session";
import { listConnections } from "../core/remote";
import type { PluginManifest } from "../registry/pluginLoader";
import { SecretInput } from "./SiteFields";
import { entryOf, putSiteValue, secretNameFor, useSiteConf, type SiteEntry } from "./siteconf";

interface Connection {
  id: string;
  host: string;
  token: string;
  repos: string[];
  filter: { state?: string; labels?: string[]; assignee?: string; kind?: string };
  twoWay: Record<string, boolean>;
  poll: string;
  webhookSecret?: string;
  workspace?: string;
}

interface Provider {
  plugin: PluginManifest;
  host: string;
  features: string[];
  connections: Connection[];
}

const POLLS = ["5m", "15m", "30m", "1h"];

function providerOf(p: PluginManifest): Provider | null {
  const props = (p.configSchema as { properties?: Record<string, Record<string, unknown>> }).properties;
  const c = props?.connections;
  if (!p.enabled || !c || c["x-drang"] !== "connections") return null;
  return {
    plugin: p,
    host: String(c["x-drang-host"] ?? ""),
    features: (c["x-drang-features"] as string[] | undefined) ?? [],
    connections: ((p.config.connections as Connection[] | undefined) ?? []).map((x) => ({
      ...x,
      repos: x.repos ?? [],
      filter: x.filter ?? {},
      twoWay: x.twoWay ?? {},
      poll: x.poll ?? "15m",
    })),
  };
}

/**
 * Settings → Integrations (spec §9.22, level `power`): connections of issue-provider plugins. The form
 * is driven by the plugin's config schema (`x-drang: "connections"`), so the host names no provider.
 * Tokens and webhook secrets are typed here and go to the secrets store (ADR 0011); the connection
 * keeps only their names, so a value from `.env` under the same name still works.
 */
export function IntegrationsSettings() {
  const { t } = useTranslation();
  const isAdmin = useSession((s) => s.session?.profile?.isAdmin);
  const workspaceId = useSession((s) => s.workspaceId);
  const [providers, setProviders] = useState<Provider[]>([]);
  /** unfolded connections; saved ones start folded, one just added starts open */
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const [saved, setSaved] = useState<string | null>(null);
  const [hooks, setHooks] = useState<Record<string, string>>({});
  // what the server found wrong with a saved connection (e.g. a token that cannot read its user)
  const [problems, setProblems] = useState<Record<string, string[]>>({});
  const site = useSiteConf(!!isAdmin);

  const refresh = useCallback(
    () =>
      fetch("/api/plugins?all=1", { credentials: "same-origin" })
        .then((r) => r.json())
        .then((b: { plugins: PluginManifest[] }) => {
          setProviders(b.plugins.map(providerOf).filter((p): p is Provider => p !== null));
          // the server knows each provider's webhook address; shown once a secret variable is saved
          return workspaceId ? listConnections(workspaceId).catch(() => []) : [];
        })
        .then((cs) => {
          setHooks(
            Object.fromEntries(
              cs.filter((c) => c.webhookUrl).map((c) => [`${c.plugin}|${c.id}`, c.webhookUrl!]),
            ),
          );
          setProblems(Object.fromEntries(cs.map((c) => [`${c.plugin}|${c.id}`, c.problems ?? []])));
        }),
    [workspaceId],
  );
  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin, refresh]);
  if (!isAdmin) return null;

  const update = (pid: string, index: number, patch: Partial<Connection> | null) =>
    setProviders((all) =>
      all.map((p) =>
        p.plugin.id !== pid
          ? p
          : {
              ...p,
              connections:
                patch === null
                  ? p.connections.filter((_, i) => i !== index)
                  : p.connections.map((c, i) => (i === index ? { ...c, ...patch } : c)),
            },
      ),
    );

  const add = (p: Provider) => {
    setOpen((o) => new Set(o).add(`${p.plugin.id}-${p.connections.length}`));
    setProviders((all) =>
      all.map((x) =>
        x.plugin.id !== p.plugin.id
          ? x
          : {
              ...x,
              connections: [
                ...x.connections,
                {
                  id: `${p.plugin.id.replace(/^tasktype-/, "")}-${x.connections.length + 1}`,
                  host: p.host,
                  token: "",
                  repos: [],
                  filter: { state: "open" },
                  twoWay: { doneOnClose: true },
                  poll: "15m",
                  workspace: workspaceId ?? undefined,
                },
              ],
            },
      ),
    );
  };

  const save = async (p: Provider) => {
    const connections = p.connections.map((c) => ({
      ...c,
      repos: c.repos.map((r) => r.trim()).filter(Boolean),
    }));
    const res = await fetch(`/api/internal/plugins/${p.plugin.id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ config: { ...p.plugin.config, connections } }),
    });
    if (res.ok) {
      setSaved(p.plugin.id);
      void refresh();
    }
  };

  return (
    <SettingsSection
      id="settings-integrations"
      intro={t("integrations.hint")}
      summary={t("settings.summary.integrations")}
      title={t("integrations.title")}
      testId="settings-integrations"
      folded
    >
      {providers.length === 0 && (
        <p className="text-[13px] text-neutral-500">{t("integrations.enableFirst")}</p>
      )}
      {providers.map((p) => (
        <div
          key={p.plugin.id}
          className="flex flex-col gap-3"
          data-testid="integration"
          data-plugin={p.plugin.id}
        >
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13px]">{p.plugin.id}</span>
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              onClick={() => add(p)}
              data-testid="integration-add"
            >
              {t("integrations.add")}
            </Button>
          </div>
          {p.connections.length === 0 && (
            <p className="text-[13px] text-neutral-500">{t("integrations.none")}</p>
          )}
          {p.connections.map((c, i) => {
            const key = `${p.plugin.id}-${i}`;
            const isOpen = open.has(key);
            return (
              <div
                key={key}
                className="rounded-md border border-neutral-200 dark:border-neutral-800"
                data-testid="connection"
                data-connection-id={c.id}
                data-open={isOpen}
              >
                {/* folded, a connection is one line: its name and host */}
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggle(key)}
                  data-testid="connection-toggle"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-neutral-100/60 dark:hover:bg-neutral-800/60"
                >
                  <ChevronRight
                    size={13}
                    aria-hidden
                    className={clsx(
                      "shrink-0 text-neutral-500 transition-transform motion-reduce:transition-none",
                      isOpen && "rotate-90",
                    )}
                  />
                  <span className="font-mono text-[13px]">{c.id || t("integrations.unnamed")}</span>
                  <span className="truncate text-[12px] text-neutral-400">{c.host}</span>
                </button>
                {isOpen && (
                  <div className="grid grid-cols-1 gap-3 border-t border-neutral-200 p-3 sm:grid-cols-2 dark:border-neutral-800">
                    <Field label={t("integrations.id")} htmlFor={`${key}-id`}>
                      <Input
                        id={`${key}-id`}
                        value={c.id}
                        onChange={(e) => update(p.plugin.id, i, { id: e.target.value })}
                      />
                    </Field>
                    <Field label={t("integrations.host")} htmlFor={`${key}-host`}>
                      <Input
                        id={`${key}-host`}
                        value={c.host}
                        onChange={(e) => update(p.plugin.id, i, { host: e.target.value })}
                      />
                    </Field>
                    <Field
                      label={t("integrations.repos")}
                      hint={t("integrations.reposHint")}
                      htmlFor={`${key}-repos`}
                    >
                      <Textarea
                        id={`${key}-repos`}
                        rows={2}
                        spellCheck={false}
                        className="font-mono text-[12.5px]"
                        value={c.repos.join("\n")}
                        onChange={(e) => update(p.plugin.id, i, { repos: e.target.value.split("\n") })}
                      />
                    </Field>
                    <ConnectionSecret
                      label={t("integrations.token")}
                      hint={t("integrations.tokenHint")}
                      name={c.token || secretNameFor(p.plugin.id, c.id, "TOKEN")}
                      entry={entryOf(site.snap, c.token || secretNameFor(p.plugin.id, c.id, "TOKEN"))}
                      onName={(token) => update(p.plugin.id, i, { token })}
                      onChanged={() => {
                        if (!c.token)
                          update(p.plugin.id, i, { token: secretNameFor(p.plugin.id, c.id, "TOKEN") });
                        void site.refresh(); // not the connections: an unsaved one would be lost
                      }}
                      testId={`${key}-token`}
                    />
                    <Field
                      label={t("integrations.labels")}
                      hint={t("integrations.labelsHint")}
                      htmlFor={`${key}-labels`}
                    >
                      <Input
                        id={`${key}-labels`}
                        value={(c.filter.labels ?? []).join(", ")}
                        onChange={(e) =>
                          update(p.plugin.id, i, {
                            filter: {
                              ...c.filter,
                              labels: e.target.value
                                .split(",")
                                .map((x) => x.trim())
                                .filter(Boolean),
                            },
                          })
                        }
                      />
                    </Field>
                    <Field label={t("integrations.kind")} htmlFor={`${key}-kind`}>
                      <Select
                        id={`${key}-kind`}
                        value={c.filter.kind ?? "all"}
                        onChange={(e) =>
                          update(p.plugin.id, i, { filter: { ...c.filter, kind: e.target.value } })
                        }
                      >
                        <option value="all">{t("integrations.kindAll")}</option>
                        <option value="issue">{t("integrations.kindIssue")}</option>
                        <option value="pr">{t("integrations.kindPr")}</option>
                      </Select>
                    </Field>
                    <Field label={t("integrations.poll")} htmlFor={`${key}-poll`}>
                      <Select
                        id={`${key}-poll`}
                        value={c.poll}
                        onChange={(e) => update(p.plugin.id, i, { poll: e.target.value })}
                      >
                        {POLLS.map((v) => (
                          <option key={v} value={v}>
                            {v.endsWith("h")
                              ? t("integrations.pollHour")
                              : t("integrations.pollMinutes", { n: Number(v.slice(0, -1)) })}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    {p.features.includes("webhook") && (
                      <ConnectionSecret
                        label={t("integrations.webhookSecret")}
                        hint={
                          hooks[`${p.plugin.id}|${c.id}`]
                            ? `${t("integrations.webhookUrl")}: ${hooks[`${p.plugin.id}|${c.id}`]}`
                            : t("integrations.webhookHint")
                        }
                        name={c.webhookSecret || secretNameFor(p.plugin.id, c.id, "WEBHOOK_SECRET")}
                        entry={entryOf(
                          site.snap,
                          c.webhookSecret || secretNameFor(p.plugin.id, c.id, "WEBHOOK_SECRET"),
                        )}
                        onName={(webhookSecret) => update(p.plugin.id, i, { webhookSecret })}
                        onChanged={() => {
                          if (!c.webhookSecret)
                            update(p.plugin.id, i, {
                              webhookSecret: secretNameFor(p.plugin.id, c.id, "WEBHOOK_SECRET"),
                            });
                          void site.refresh(); // not the connections: an unsaved one would be lost
                        }}
                        generate
                        testId={`${key}-hook`}
                      />
                    )}
                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                      <Toggle
                        label={t("integrations.assignedToMe")}
                        checked={c.filter.assignee === "me"}
                        onChange={(v) =>
                          update(p.plugin.id, i, { filter: { ...c.filter, assignee: v ? "me" : "" } })
                        }
                      />
                      {["doneOnClose", "closeOnDone", "timeToRemote", "assignOnStart"]
                        .filter((f) => p.features.includes(f))
                        .map((f) => (
                          <Toggle
                            key={f}
                            label={t(`integrations.${f}`)}
                            checked={Boolean(c.twoWay[f])}
                            onChange={(v) => update(p.plugin.id, i, { twoWay: { ...c.twoWay, [f]: v } })}
                          />
                        ))}
                      {(problems[`${p.plugin.id}|${c.id}`] ?? [])
                        .filter((k) =>
                          k === "remote.assigneeUnknown"
                            ? c.filter.assignee === "me"
                            : k === "remote.assignOnStartUnknown"
                              ? Boolean(c.twoWay.assignOnStart)
                              : true,
                        )
                        .map((k) => (
                          <p
                            key={k}
                            className="rounded-sm bg-warn-tint px-2 py-1 text-[12.5px] text-warn"
                            role="status"
                            data-testid="integration-problem"
                          >
                            {t(`integrations.problem.${k.replace(/^remote\./, "")}`)}
                          </p>
                        ))}
                    </div>
                    {/* saving lives with the connection: it saves this provider's connections */}
                    <div className="flex items-center gap-3 sm:col-span-2">
                      <Button size="sm" onClick={() => void save(p)} data-testid="integration-save">
                        {t("integrations.save")}
                      </Button>
                      {saved === p.plugin.id && (
                        <span className="text-[12.5px] text-neutral-500" role="status">
                          {t("integrations.saved")}
                        </span>
                      )}
                      {/* a removed connection has no Save of its own any more: asked once, saved at once */}
                      <span className="ml-auto">
                        <ConfirmButton
                          size="sm"
                          variant="secondary"
                          aria-label={t("integrations.removeLabel", { id: c.id })}
                          question={t("integrations.removeConfirm", { id: c.id || "?" })}
                          onConfirm={() => {
                            setOpen(new Set()); // positions shift after a removal
                            void save({ ...p, connections: p.connections.filter((_, j) => j !== i) });
                          }}
                        >
                          {t("integrations.remove")}
                        </ConfirmButton>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </SettingsSection>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
}) {
  return (
    <span className="flex items-center gap-2 text-[13px]">
      <Checkbox checked={checked} label={label} onChange={onChange} />
      <span aria-hidden>{label}</span>
    </span>
  );
}

/**
 * A connection's token or webhook secret: typed here (write-only, stored as a secret under `name`);
 * the name itself can be changed under "Stored as", e.g. to use a value that already sits in `.env`.
 * With `generate`, Drang can make a random secret and shows it once, to paste into the provider.
 */
function ConnectionSecret({
  label,
  hint,
  name,
  entry,
  onName,
  onChanged,
  generate,
  testId,
}: {
  label: string;
  hint: string;
  name: string;
  entry: SiteEntry | undefined;
  onName(name: string): void;
  onChanged(): void;
  generate?: boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState<string | null>(null);
  const e: SiteEntry = entry ?? { name, secret: true, source: "unset", envSet: false, updatedAt: null };
  const makeOne = async () => {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    const value = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    await putSiteValue(name, value);
    setShown(value);
    onChanged();
  };
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-[12.5px] text-neutral-500">{label}</span>
      <SecretInput entry={e} label={label} onChanged={onChanged} />
      {generate && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => void makeOne()} data-testid="secret-generate">
            {t("siteconf.generate")}
          </Button>
          {shown && (
            <Input
              compact
              readOnly
              className="min-w-0 flex-1 font-mono"
              value={shown}
              aria-label={t("siteconf.generated")}
              onFocus={(ev) => ev.target.select()}
              data-testid="secret-generated"
            />
          )}
        </div>
      )}
      {shown && <p className="text-[12px] text-neutral-500">{t("siteconf.generatedHint")}</p>}
      <label className="flex items-center gap-1.5 whitespace-nowrap text-[12px] text-neutral-500">
        {t("siteconf.storedAs")}
        <Input
          compact
          spellCheck={false}
          className="min-w-0 flex-1 font-mono text-[12px]"
          value={name}
          onChange={(ev) => onName(ev.target.value.toUpperCase())}
        />
      </label>
      <p className="text-[12px] text-neutral-500">{hint}</p>
    </div>
  );
}
